/**
 * Mutate trip.hotelStaysByCity + taskItems — used by Trip Lodging UI.
 */

import type { Trip, TripHotelStay } from '../types/client';
import {
  removeHotelConfirmationTripTask,
  syncHotelConfirmationTripTask,
} from './tripTaskTriggers';
import { lodgingSaveTargetCityVisitId } from './cruiseLodgingCityVisitFilter';
import { normalizeConfirmations } from './hotelStayHelpers';

export function upsertHotelStayOnTrip(params: {
  trip: Trip;
  cityVisitId: string;
  stay: TripHotelStay;
  /** When editing, remove from previous city if the save target moved. */
  previousCityVisitId?: string | null;
}): Partial<Pick<Trip, 'hotelStaysByCity' | 'taskItems'>> {
  const confirmations = normalizeConfirmations(params.stay.confirmations);
  const stay: TripHotelStay = { ...params.stay, confirmations };
  const checkIn = (stay.checkInDate ?? '').slice(0, 10);
  const targetCityId = lodgingSaveTargetCityVisitId(
    params.trip,
    params.cityVisitId,
    checkIn || new Date().toISOString().slice(0, 10)
  );

  const byCity: Record<string, TripHotelStay[]> = {
    ...(params.trip.hotelStaysByCity ?? {}),
  };

  const removeFrom = (cityId: string): void => {
    const list = byCity[cityId];
    if (!list) return;
    const next = list.filter((s) => s.id !== stay.id);
    if (next.length === 0) {
      delete byCity[cityId];
    } else {
      byCity[cityId] = next;
    }
  };

  if (params.previousCityVisitId && params.previousCityVisitId !== targetCityId) {
    removeFrom(params.previousCityVisitId);
  }
  // Also remove from any other city that might still hold this id
  for (const cityId of Object.keys(byCity)) {
    if (cityId !== targetCityId) removeFrom(cityId);
  }

  const existing = byCity[targetCityId] ?? [];
  const idx = existing.findIndex((s) => s.id === stay.id);
  if (idx >= 0) {
    const next = [...existing];
    next[idx] = stay;
    byCity[targetCityId] = next;
  } else {
    byCity[targetCityId] = [...existing, stay];
  }

  const taskItems = syncHotelConfirmationTripTask({
    taskItems: params.trip.taskItems,
    hotelStayId: stay.id,
    hotelName: stay.hotelName,
    confirmations: stay.confirmations,
    reservationId: stay.reservationId,
  });

  return { hotelStaysByCity: byCity, taskItems };
}

export function deleteHotelStayFromTrip(params: {
  trip: Trip;
  cityVisitId: string;
  hotelStayId: string;
}): Partial<Pick<Trip, 'hotelStaysByCity' | 'taskItems'>> {
  const byCity: Record<string, TripHotelStay[]> = {
    ...(params.trip.hotelStaysByCity ?? {}),
  };
  for (const cityId of Object.keys(byCity)) {
    const next = byCity[cityId].filter((s) => s.id !== params.hotelStayId);
    if (next.length === 0) delete byCity[cityId];
    else byCity[cityId] = next;
  }
  const taskItems = removeHotelConfirmationTripTask({
    taskItems: params.trip.taskItems,
    hotelStayId: params.hotelStayId,
  });
  return { hotelStaysByCity: byCity, taskItems };
}
